import React, { useState, createRef, useLayoutEffect, useEffect } from 'react';
import { connect } from 'react-redux';

import HighchartsReact from 'highcharts-react-official';
import Utils from '../../utils/Utils';
import HighchartsExporting from 'highcharts/modules/exporting';
import Highcharts from 'highcharts';
import HighChartsMore from 'highcharts/highcharts-more';
import HighChartsNoData from 'highcharts/modules/no-data-to-display';

import { Slider } from '@mui/material';
import { SliderValueLabel } from './components/SliderValueLabel';
import { Panning } from './components/Panning';
import { Zooming } from './components/Zooming';

import moment from 'moment';
import { extendMoment } from 'moment-range';
import { debounce } from 'throttle-debounce';

import {
  dateTimeDailyOrMonthlyRounding,
  dateTimeRangeRounding,
} from '../ReportRenderer/utils/ReportDateTimeUtils';

const momentExtended = extendMoment(moment);

function ChartEnhanced(props) {
  initReactHighCharts();
  //init local variables
  initMomentFormat(props);

  let chartEnhanced = createRef();
  const sliderMin =
      props.config.Slider != undefined && props.config.Slider.Min != undefined
        ? calcSliderFromTimeGranurality(
            moment.duration(+props.config.Slider.Min, 'minutes').asMinutes()
          )
        : 0.430676558,
    sliderMax =
      props.config.Slider != undefined && props.config.Slider.Max != undefined
        ? calcSliderFromTimeGranurality(
            moment.duration(+props.config.Slider.Max, 'minutes').asMinutes()
          )
        : 8.615084074,
    sliderStep = 0.001;

  let maxDateTo = props.maxDateTo ? props.maxDateTo : moment.utc().startOf('day');

  const steps = [
    {
      key: 0,
      name: '2m',
      label: '2m',
      value: calcSliderFromTimeGranurality(moment.duration(2, 'minutes').asMinutes()),
    },
    {
      key: 1,
      name: '10m',
      label: '10m',
      value: calcSliderFromTimeGranurality(moment.duration(10, 'minutes').asMinutes()),
    },
    {
      key: 2,
      name: '1h',
      label: '1h',
      value: calcSliderFromTimeGranurality(moment.duration(1, 'hours').asMinutes()),
    },
    {
      key: 3,
      name: '5h',
      label: '5h',
      value: calcSliderFromTimeGranurality(moment.duration(5, 'hours').asMinutes()),
    },
    {
      key: 4,
      name: '1d',
      label: '1d',
      value: calcSliderFromTimeGranurality(moment.duration(1, 'days').asMinutes()),
    },
    {
      key: 5,
      name: '1w',
      label: '1w',
      value: calcSliderFromTimeGranurality(moment.duration(1, 'weeks').asMinutes()),
    },
    {
      key: 6,
      name: '1M',
      label: '1M',
      value: calcSliderFromTimeGranurality(moment.duration(1, 'months').asMinutes()),
    },
    {
      key: 7,
      name: '6M',
      label: '6M',
      value: calcSliderFromTimeGranurality(moment.duration(6, 'months').asMinutes()),
    },

    {
      key: 8,
      name: '2Y',
      label: '2Y',
      value: calcSliderFromTimeGranurality(moment.duration(2, 'years').asMinutes()),
    },
  ];

  //declare state variables
  const [slider, setSlider] = useState(calcSliderFromTimeGranurality(props.activeTimeGran));

  useEffect(() => {
    setSlider(calcSliderFromTimeGranurality(props.activeTimeGran));
  }, [props.activeTimeGran]);

  useLayoutEffect(() => {
    let chart = chartEnhanced.current ? chartEnhanced.current.chart : null;

    if (chart) {
      chart.options.exporting.sourceWidth = chart.chartWidth;
      chart.options.exporting.sourceHeight = chart.chartHeight;
      chart.options.exporting.filename = chart.title.textStr.replace(/\s+/g, '-').toLowerCase();
    }
  });

  const handleSliderChange = debounce(50, (e) => {
    let roundedRange = handleGranuralityChange(calcTimeGranuralityFromSlider(e), props);
    handleStatePropagation(roundedRange, calcTimeGranuralityFromSlider(e), props);
  });

  return props.dateTimeError ? (
    <p className="u-ml-double">No data to show</p>
  ) : (
    <div>
      <div>
        <div className="u-fx u-fx-column ">
          <div className="u-fx u-fx-row u-fx-align-center u-fx-space-between">
            <div>
              <Panning
                sliderPeriod={calcTimeGranuralityFromSlider(slider)}
                direction="left"
                onClick={(newDateRange) => {
                  handleStatePropagation(
                    newDateRange,
                    calcTimeGranuralityFromSlider(slider),
                    props
                  );
                }}
                roundDateTime={roundDateTime}
                maxDateTo={maxDateTo}
                {...props}
              />
            </div>
            <div
              style={{
                width: '90%',
                overflow: 'hidden',
              }}
              data-qa="qa-chart"
            >
              <HighchartsReact
                options={props.config}
                ref={chartEnhanced}
                highcharts={Highcharts}
                immutable={true}
                constructorType="chart"
              />
            </div>
            <div>
              <Panning
                sliderPeriod={calcTimeGranuralityFromSlider(slider)}
                direction="right"
                onClick={(newDateRange) => {
                  handleStatePropagation(
                    newDateRange,
                    calcTimeGranuralityFromSlider(slider),
                    props
                  );
                }}
                roundDateTime={roundDateTime}
                maxDateTo={maxDateTo}
                {...props}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="u-fx u-fx-column ">
        <div className="u-fx u-fx-row u-fx-align-center u-fx-space-between ">
          <div>
            <Zooming
              disabled={slider === sliderMin || slider < sliderMin}
              direction="in"
              onClick={() => {
                let dec = getSliderInc(calcTimeGranuralityFromSlider(slider));
                handleSliderChange(slider - dec);
              }}
              zoomText={getZoomText('zoom-in', slider, props)}
            />
          </div>
          <div style={{ width: '90%' }}>
            <Slider
              style={{ margin: '0' }}
              valueLabelFormat={(value) => {
                return moment.duration(calcTimeGranuralityFromSlider(value)).humanize();
              }}
              aria-labelledby="discrete-slider-custom"
              valueLabelDisplay="auto"
              marks={steps.filter((data) => data.value >= sliderMin && data.value <= sliderMax)}
              min={sliderMin}
              max={sliderMax}
              value={slider}
              step={sliderStep}
              onChange={(e, newValue) => {
                handleSliderChange(newValue);
              }}
              data-qa="qa-slider"
              components={{
                ValueLabel: SliderValueLabel,
              }}
            />
          </div>
          <div>
            <Zooming
              disabled={slider === sliderMax || slider > sliderMax}
              direction="out"
              onClick={() => {
                let inc = getSliderInc(calcTimeGranuralityFromSlider(slider));
                handleSliderChange(slider + inc);
              }}
              zoomText={getZoomText('zoom-out', slider, props)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function initReactHighCharts() {
  if (typeof Highcharts === 'object') {
    HighchartsExporting(Highcharts);
    HighChartsMore(Highcharts);
    HighChartsNoData(Highcharts);

    Highcharts.SVGRenderer.prototype.symbols.cross = function (x, y, w, h) {
      return ['M', x, y, 'L', x + w, y + h, 'M', x + w, y, 'L', x, y + h, 'z'];
    };

    Highcharts.SVGRenderer.prototype.symbols.slash = function (x, y, w, h) {
      return ['M', x + w, y + h, 'L', x + w, y + h, 'M', x + w, y, 'L', x, y + h, 'z'];
    };

    Highcharts.dateFormats = {
      E: Utils.determineDaysHourMinSecFromTicks,
    };

    Highcharts.setOptions({
      lang: {
        noData: 'No data to show within selected time range',
      },
      plotOptions: {
        series: {
          animation: false,
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
            ],
          },
        },
      },
    });

    Highcharts.defaultOptions.credits.enabled = false;
  }
}

function initMomentFormat(props) {
  let startType =
    props.config.Slider && props.config.Slider.mode && props.config.Slider.startType
      ? props.config.Slider.startType
      : '';
  //seetings for moment humanize method
  moment.relativeTimeThreshold('m', 60); //least number of minutes to be considered an hour
  moment.relativeTimeThreshold('h', 24); //least number of hours to be considered a day

  //if in daily view don't round up to nr of month
  if (startType == 'date') {
    moment.relativeTimeThreshold('d', 183); //least number of days to be considered a month
  } else if (startType == 'month') {
    moment.relativeTimeThreshold('d', 25); //least number of days to be considered a month
  }

  moment.relativeTimeThreshold('M', 24); //least number of months to be considered a year
}

function calcTimeGranuralityFromSlider(sliderValue) {
  let timeValueMins = Math.pow(5, sliderValue);
  let period = moment.duration(timeValueMins, 'minutes');
  return period;
}

function calcSliderFromTimeGranurality(timeValue) {
  let timeValueMins = moment.isDuration(timeValue) ? timeValue.asMinutes() : timeValue;
  let sliderVal = Math.log(timeValueMins) / Math.log(5);
  return sliderVal;
}

function handleGranuralityChange(sliderPeriod, props) {
  const center = momentExtended.range(props.dateTimeFrom, props.dateTimeTo).center();
  const maxDateTo = props.maxDateTo ? props.maxDateTo : moment.utc().startOf('day');

  let newDateFrom = moment(center)
    .subtract(sliderPeriod.asMinutes() / 2, 'minutes')
    .seconds(0);

  let newDateTo = moment(center)
    .add(sliderPeriod.asMinutes() / 2, 'minutes')
    .seconds(0);

  if (
    !moment(props.dateTimeFrom).isSame(newDateFrom) &&
    !moment(props.dateTimeTo).isSame(newDateTo)
  ) {
    if (newDateTo.isAfter(maxDateTo)) {
      let spill = moment.duration(newDateTo.diff(maxDateTo, 'minutes'), 'minutes');
      newDateFrom.subtract(spill, 'minutes').seconds(0);

      return roundDateTime(props, newDateFrom.toDate(), moment(maxDateTo).toDate());
    } else {
      return roundDateTime(props, newDateFrom.toDate(), newDateTo.toDate());
    }
  }
}

function getSliderInc(timeValue) {
  var sliderIncLevels = [
    {
      value: 0.5,
      start: moment.duration(0, 'minutes').asMinutes(),
      end: moment.duration(2, 'minutes').asMinutes(),
    },
    {
      value: 0.2,
      start: moment.duration(2, 'minutes').asMinutes(),
      end: moment.duration(60, 'minutes').asMinutes(),
    },
    {
      value: 0.1,
      start: moment.duration(60, 'minutes').asMinutes(),
      end: moment.duration(2.5, 'years').asMinutes(),
    },
  ];

  let sliderInc = sliderIncLevels.find(
    (el) =>
      moment.duration(el.start) <= timeValue.asMinutes() &&
      moment.duration(el.end) > timeValue.asMinutes()
  );

  //time range out of 2 years
  if (sliderInc === undefined) {
    sliderInc = sliderIncLevels[2];
  }

  return sliderInc.value;
}

function getZoomText(direction, slider, props) {
  if (isNaN(slider) || slider == undefined) {
    return '';
  }

  let currentInc = getSliderInc(calcTimeGranuralityFromSlider(slider));
  let currentTimePeriod = calcTimeGranuralityFromSlider(slider).asMinutes();
  let period;

  switch (direction) {
    case 'zoom-out': {
      let nextTimeRange = handleGranuralityChange(
        calcTimeGranuralityFromSlider(slider + currentInc),
        props
      );
      let nextTimeGran = moment
        .duration(
          moment(nextTimeRange.dateTo).diff(moment(nextTimeRange.dateFrom), 'minutes'),
          'minutes'
        )
        .asMinutes();

      period = moment.duration(nextTimeGran - currentTimePeriod, 'minutes');
      break;
    }
    case 'zoom-in': {
      let nextTimeRange = handleGranuralityChange(
        calcTimeGranuralityFromSlider(slider - currentInc),
        props
      );
      let nextTimeGran = moment
        .duration(
          moment(nextTimeRange.dateTo).diff(moment(nextTimeRange.dateFrom), 'minutes'),
          'minutes'
        )
        .asMinutes();

      period = moment.duration(currentTimePeriod - nextTimeGran, 'minutes');
      break;
    }
  }

  let roundedPeriod = roundSeconds(period);

  return roundedPeriod.humanize();
}

function roundSeconds(period) {
  let seconds = period.seconds();
  if (seconds <= 30) {
    period.subtract(seconds, 'seconds');
  } else {
    period.subtract(seconds, 'seconds').add(1, 'minutes');
  }
  return period;
}

function roundDateTime(props, newDateFrom, newDateTo) {
  let newRange, newRange2;
  let queryState = {
    dateTimeFrom: props.dateTimeFrom,
    dateTimeTo: props.dateTimeTo,
  };

  if (!props.config.Slider || !props.config.Slider.type) {
    //no rounding
    newRange = {
      dateFrom: newDateFrom,
      dateTo: newDateTo,
    };
  } else {
    if (props.config.Slider.type == 'TimeRangeRounding') {
      newRange = dateTimeRangeRounding(newDateFrom, newDateTo, props.config.Slider.options);
    }

    if (props.config.Slider.type == 'TimeRange') {
      let startType = props.config.Slider.mode.startType;
      let endType = props.config.Slider.mode.endType;

      newRange = dateTimeDailyOrMonthlyRounding(
        'from',
        moment(queryState.dateTimeFrom),
        moment(queryState.dateTimeTo),
        moment.utc(newDateFrom),
        startType,
        endType
      );

      newRange2 = dateTimeDailyOrMonthlyRounding(
        'to',
        moment(newRange.dateFrom),
        moment(queryState.dateTimeTo),
        moment.utc(newDateTo),
        startType,
        endType
      );
      newRange = newRange2;
    }
  }

  return newRange;
}

function handleStatePropagation(roundedValues, sliderPeriod, props) {
  if (!props.config.Slider) {
    props.onChangeDateTime(
      'dateFromAndTo',
      roundedValues.dateFrom,
      roundedValues.dateTo,
      sliderPeriod
    );
  } else {
    props.onChangeDateTime(roundedValues);
  }
}

function mapStateToProps(state) {
  return {
    panningStep:
      state.user && state.user.Settings && state.user.Settings.PanningStep
        ? state.user.Settings.PanningStep
        : 50, // default to half of the time period if not defined
  };
}

export default connect(mapStateToProps)(ChartEnhanced);
